import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, BookOpen, Eye, EyeOff } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { validateFormField, isHoneypotFilled, HONEYPOT_FIELD, checkRateLimit, markFormLoaded, isSubmitTooFast, sanitizeText } from '../utils/formValidation';

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '', [HONEYPOT_FIELD]: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { markFormLoaded('login'); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotFilled(form[HONEYPOT_FIELD])) return;
    if (isSubmitTooFast('login')) return;
    if (!checkRateLimit('login', 3000)) { setError('Please wait a moment.'); return; }
    const emailCheck = validateFormField(form.email, 'email');
    if (!emailCheck.valid) { setError(emailCheck.error); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', { email: sanitizeText(form.email), password: form.password });
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/ghostwriter/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <>
      <SeoHead title="Login — AI Ghostwriter Pro" noindex={true} />
      <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 md:mb-10">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <BookOpen className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="font-serif text-xl text-foreground">AI Ghostwriter <span className="text-primary">Pro</span></span>
            </Link>
            <h1 className="font-serif text-2xl sm:text-3xl tracking-tight mb-2">Welcome Back</h1>
            <p className="text-sm text-muted-foreground">Sign in to manage your projects</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5" data-testid="login-form" noValidate>
            {error && <p className="text-sm text-red-400 text-center" data-testid="login-error">{error}</p>}
            <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
              <input type="text" name={HONEYPOT_FIELD} value={form[HONEYPOT_FIELD]} onChange={(e) => setForm({...form, [HONEYPOT_FIELD]: e.target.value})} autoComplete="off" tabIndex={-1} />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                data-testid="login-email" placeholder="Email" maxLength={254}
                className="w-full pl-10 pr-4 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none" />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input type={showPass ? 'text' : 'password'} required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                data-testid="login-password" placeholder="Password"
                className="w-full pl-10 pr-10 h-12 bg-white/5 border border-white/10 rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="login-toggle-password">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-password">Forgot password?</Link>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Don't have an account? <Link to="/register" className="text-primary hover:underline" data-testid="login-to-register">Create one</Link>
          </p>
        </div>
      </section>
    </>
  );
}
